import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, shareReplay } from 'rxjs';
import { saveSession } from '../../shared/utils/session.util';
import { AuthService } from './auth.service';

/**
 * Renews the session with the stored refresh token. Concurrent callers share
 * one request: the backend rotates the refresh token on every use, so two
 * parallel refreshes with the same token would make the second one fail.
 */
@Injectable({ providedIn: 'root' })
export class SessionRefreshService {
  private authService = inject(AuthService);
  private inFlight: Observable<string> | null = null;

  /** Emits the new access token, or errors when the session cannot be renewed. */
  public refresh(): Observable<string> {
    if (!this.inFlight) {
      const refreshToken = localStorage.getItem('refreshToken') ?? '';
      this.inFlight = this.authService.refresh({ refreshToken }).pipe(
        map((response) => {
          const { accessToken, refreshToken: nextRefresh } = response.result ?? {};
          if (!accessToken || !nextRefresh || !saveSession(accessToken, nextRefresh)) {
            throw new Error('Invalid refresh response');
          }
          return accessToken;
        }),
        finalize(() => (this.inFlight = null)),
        shareReplay(1),
      );
    }
    return this.inFlight;
  }
}
